/**
 * Shared client-side validation for username, email, and password.
 */

const USERNAME_RE = /^[a-zA-Z0-9._-]{3,50}$/;
const EMAIL_RE = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;
const PASSWORD_MIN = 6;
const PASSWORD_MAX = 128;

export function validateUsername(username, { required = true } = {}) {
  const value = String(username || '').trim();
  if (!value) {
    return required ? 'Username is required' : null;
  }
  if (value.length < 3) {
    return 'Username must be at least 3 characters';
  }
  if (value.length > 50) {
    return 'Username must be at most 50 characters';
  }
  if (/\s/.test(value)) {
    return 'Username cannot contain spaces';
  }
  if (!USERNAME_RE.test(value)) {
    return 'Username may only contain letters, numbers, dots, hyphens, and underscores';
  }
  return null;
}

export function validateEmail(email, { required = true } = {}) {
  const value = String(email || '').trim().toLowerCase();
  if (!value) {
    return required ? 'Email is required' : null;
  }
  if (value.length > 255) {
    return 'Email must be at most 255 characters';
  }
  if (value.includes('..') || value.startsWith('.') || value.includes('@.') || value.includes('.@')) {
    return 'Please enter a valid email address';
  }
  if (!EMAIL_RE.test(value)) {
    return 'Please enter a valid email address';
  }
  const [local, domain] = value.split('@');
  if (!local || !domain || local.length > 64) {
    return 'Please enter a valid email address';
  }
  const tld = domain.split('.').pop();
  if (!tld || tld.length < 2 || !/^[a-z]+$/i.test(tld)) {
    return 'Please enter a valid email address (e.g. name@gmail.com)';
  }
  return null;
}

export function validatePassword(password, { required = true, fieldLabel = 'Password' } = {}) {
  const value = String(password || '');
  if (!value) {
    return required ? `${fieldLabel} is required` : null;
  }
  if (value.length < PASSWORD_MIN) {
    return `${fieldLabel} must be at least ${PASSWORD_MIN} characters`;
  }
  if (value.length > PASSWORD_MAX) {
    return `${fieldLabel} must be at most ${PASSWORD_MAX} characters`;
  }
  if (/\s/.test(value)) {
    return `${fieldLabel} cannot contain spaces`;
  }
  return null;
}

export function validateUserFields({ username, email, password, passwordOptional = false } = {}) {
  return (
    validateUsername(username) ||
    validateEmail(email) ||
    validatePassword(password, { required: !passwordOptional }) ||
    null
  );
}

export const VALIDATION = {
  USERNAME_RE,
  EMAIL_RE,
  PASSWORD_MIN,
  PASSWORD_MAX
};
